<template>
    <section class="templates-page mt-16">
        <v-row>
            <v-col
                cols="12"
                offset="0"
                md="10"
                offset-md="1"
            >
                <!-- ===================================================== -->
                <!-- TOPO                                                  -->
                <!-- ===================================================== -->
                <div class="templates-top">
                    <v-breadcrumbs
                        large
                        :items="crumbs"
                        divider="/"
                        light
                        class="pl-0 templates-breadcrumbs"
                    />

                    <div class="templates-titlebar">
                        <div>
                            <div class="templates-title">
                                <div class="templates-title__icon">
                                    <v-icon color="primary">
                                        mdi-file-table-box-multiple-outline
                                    </v-icon>
                                </div>

                                <div>
                                    <h1 class="primary--text mb-0">
                                        Templates
                                    </h1>

                                    <div class="templates-subtitle">
                                        Gere e atualize os templates das obras
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="templates-titlebar__actions">
                            <v-btn
                                color="primary"
                                class="create-btn mr-3"
                                dark
                                @click="createTemplate"
                            >
                                <v-icon left>
                                    mdi-plus
                                </v-icon>

                                Criar template
                            </v-btn>
                        </div>
                    </div>
                </div>

                <!-- ===================================================== -->
                <!-- CARD PRINCIPAL                                        -->
                <!-- ===================================================== -->
                <v-card
                    class="templates-card mt-8"
                    elevation="10"
                >
                    <!-- HEADER -->
                    <v-card-title
                        class="templates-card__header primary white--text"
                    >
                        <div class="d-flex align-center">
                            <div class="header-icon">
                                <v-icon color="white">
                                    mdi-view-list-outline
                                </v-icon>
                            </div>

                            <div>
                                <div class="font-weight-bold">
                                    Listagem de templates
                                </div>

                                <div class="header-subtitle">
                                    Templates configurados por obra
                                </div>
                            </div>
                        </div>

                        <v-spacer />

                        <div class="d-flex align-center">
                            <div
                                v-if="lastUpdatedText"
                                class="last-updated mr-4 d-none d-md-flex"
                            >
                                <v-icon
                                    small
                                    color="white"
                                    class="mr-1"
                                >
                                    mdi-clock-outline
                                </v-icon>

                                {{ lastUpdatedText }}
                            </div>

                            <v-chip
                                color="white"
                                outlined
                                class="templates-count-chip"
                            >
                                <v-icon left small>
                                    mdi-format-list-numbered
                                </v-icon>

                                {{ total }} {{ total === 1 ? 'registo' : 'registos' }}
                            </v-chip>
                        </div>
                    </v-card-title>

                    <!-- BODY -->
                    <v-card-text class="templates-card__body">
                        <!-- MINI TOOLBAR -->
                        <div class="table-toolbar">
                            <div class="table-toolbar__info">
                                <div class="table-toolbar__icon">
                                    <v-icon
                                        small
                                        color="primary"
                                    >
                                        mdi-information-outline
                                    </v-icon>
                                </div>

                                <div>
                                    <div class="table-toolbar__title">
                                        Templates disponíveis
                                    </div>

                                    <div class="table-toolbar__subtitle">
                                        Selecione o lápis para editar um template.
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- ================================================= -->
                        <!-- TABELA                                            -->
                        <!-- ================================================= -->
                        <div class="templates-table-wrapper">
                            <v-data-table
                                :headers="headers"
                                :items="items"
                                :server-items-length="total"
                                :loading="loading"
                                :footer-props="tableFooter"
                                :options.sync="options"
                                class="templates-table"
                                locale="pt-pt"
                            >
                                <!-- LOADING -->
                                <template v-slot:progress>
                                    <v-progress-linear
                                        color="primary"
                                        indeterminate
                                    />
                                </template>

                                <!-- OBRA -->
                                <template v-slot:item.Obra.Descricao="{ item }">
                                    <div class="work-cell">
                                        <div class="work-cell__icon">
                                            <v-icon
                                                small
                                                color="primary"
                                            >
                                                mdi-domain
                                            </v-icon>
                                        </div>

                                        <div>
                                            <div class="work-cell__name">
                                                {{
                                                    item.Obra && item.Obra.Descricao
                                                        ? item.Obra.Descricao
                                                        : '-'
                                                }}
                                            </div>

                                            <div
                                                v-if="item.Obra && item.Obra.Codigo"
                                                class="work-cell__code"
                                            >
                                                {{ item.Obra.Codigo }}
                                            </div>
                                        </div>
                                    </div>
                                </template>

                                <!-- FRENTE -->
                                <template v-slot:item.frente_obra="{ item }">
                                    {{
                                        item.Frente == null
                                            ? '-'
                                            : item.Frente.Descricao
                                    }}
                                </template>

                                <!-- CLASSE -->
                                <template v-slot:item.classe_obra="{ item }">
                                    {{
                                        item.Classe == null
                                            ? '-'
                                            : item.Classe.Descricao
                                    }}
                                </template>

                                <!-- UTILIZADOR -->
                                <template v-slot:item.user="{ item }">
                                    <div
                                        v-if="item.user != null"
                                        class="user-cell"
                                    >
                                        <div class="user-avatar">
                                            {{
                                                getUserInitials(item.user.name)
                                            }}
                                        </div>

                                        <div class="user-cell__content">
                                            <div class="user-cell__name">
                                                {{ item.user.name }}
                                            </div>

                                            <div
                                                v-if="item.user.funcionario_codigo"
                                                class="user-cell__code"
                                            >
                                                Nº {{ item.user.funcionario_codigo }}
                                            </div>
                                        </div>
                                    </div>

                                    <span
                                        v-else
                                        class="grey--text"
                                    >
                                        —
                                    </span>
                                </template>

                                <!-- AÇÕES -->
                                <template v-slot:item.actions="{ item }">
                                    <div class="actions-cell">
                                        <template v-if="!item.locked">
                                            <!-- EDITAR -->
                                            <v-tooltip bottom>
                                                <template
                                                    v-slot:activator="{ on, attrs }"
                                                >
                                                    <v-btn
                                                        v-if="$store.getters.hasPermission([
                                                            'super',
                                                            'templates.update'
                                                        ])"
                                                        icon
                                                        small
                                                        class="action-btn action-btn--edit"
                                                        v-bind="attrs"
                                                        v-on="on"
                                                        @click="viewTemplate(item)"
                                                    >
                                                        <v-icon small>
                                                            mdi-pencil-outline
                                                        </v-icon>
                                                    </v-btn>
                                                </template>

                                                <span>Editar template</span>
                                            </v-tooltip>

                                            <!-- ELIMINAR -->
                                            <div
                                                v-if="$store.getters.hasPermission([
                                                    'super',
                                                    'templates.delete'
                                                ])"
                                                class="remove-wrapper"
                                            >
                                                <IconRemove
                                                    @on-submit="deleteTemplate(item)"
                                                />
                                            </div>
                                        </template>

                                        <!-- BLOQUEADO -->
                                        <v-tooltip
                                            v-else
                                            bottom
                                        >
                                            <template
                                                v-slot:activator="{ on, attrs }"
                                            >
                                                <div
                                                    class="locked-chip"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    <v-icon
                                                        small
                                                        class="mr-1"
                                                    >
                                                        mdi-lock-outline
                                                    </v-icon>

                                                    Bloqueado
                                                </div>
                                            </template>

                                            <span>
                                                Este template está a ser processado
                                            </span>
                                        </v-tooltip>
                                    </div>
                                </template>

                                <!-- SEM DADOS -->
                                <template v-slot:no-data>
                                    <div class="empty-state">
                                        <div class="empty-state__icon">
                                            <v-icon
                                                size="42"
                                                color="grey lighten-1"
                                            >
                                                mdi-file-search-outline
                                            </v-icon>
                                        </div>

                                        <div class="empty-state__title">
                                            Sem templates
                                        </div>

                                        <div class="empty-state__text">
                                            Não foram encontrados templates para apresentar.
                                        </div>

                                        <v-btn
                                            small
                                            color="primary"
                                            outlined
                                            class="mt-4"
                                            @click="refreshTable"
                                        >
                                            <v-icon left small>
                                                mdi-refresh
                                            </v-icon>

                                            Atualizar
                                        </v-btn>
                                    </div>
                                </template>
                            </v-data-table>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- ============================================================= -->
        <!-- SUCESSO                                                       -->
        <!-- ============================================================= -->
        <DialogSuccess
            :opened="success"
            @on-ok="onSuccess"
        >
            Template removido com sucesso!
        </DialogSuccess>
    </section>
</template>

<script>
import Template from "@/api/Templates.js";
import dayjs from "dayjs";

import IconRemove from "@/components/UI/IconRemove.vue";
import DialogSuccess from "@/components/UI/DialogSuccess.vue";

export default {
    name: "TemplatesList",

    components: {
        IconRemove,
        DialogSuccess
    },

    data: () => ({
        loading: false,
        searching: false,

        updateDialog: {
            show: false,
            linha: null
        },

        items: [],

        options: {
            page: 1,
            itemsPerPage: 10,
            sortBy: [],
            sortDesc: []
        },

        filter: {},

        total: 0,

        success: false,

        lastUpdatedAt: null,

        searchTimeout: null,

        headers: [
            {
                text: "Obra",
                align: "start",
                value: "Obra.Descricao"
            },
            {
                text: "Utilizador",
                align: "start",
                value: "user",
                sortable: false
            },
            {
                text: "Opções",
                align: "center",
                value: "actions",
                sortable: false,
                width: "180px"
            }
        ],

        tableFooter: {
            "items-per-page-options": [5, 10, 15, 20],
            "items-per-page-text": "Linhas por página"
        }
    }),

    computed: {
        crumbs() {
            return [
                {
                    text: "Dashboard",
                    disabled: false,
                    to: "/admin",
                    exact: true
                },
                {
                    text: "Templates",
                    disabled: true,
                    to: window.location.href,
                    exact: true
                }
            ];
        },

        lastUpdatedText() {
            if (!this.lastUpdatedAt) {
                return "";
            }

            return `Atualizado às ${dayjs(this.lastUpdatedAt).format("HH:mm:ss")}`;
        }
    },

    watch: {
        options: {
            deep: true,

            handler() {
                /*
                 * Pequeno debounce porque o v-data-table pode emitir
                 * mais do que uma alteração de options em sequência.
                 */
                clearTimeout(this.searchTimeout);

                this.searchTimeout = setTimeout(() => {
                    this.searchTemplate();
                }, 100);
            }
        }
    },

    mounted() {
        if (
            !this.$store.getters.hasPermission([
                "super",
                "templates.read"
            ])
        ) {
            this.$router.push("/forbidden");
            return;
        }

        this.fillBaseData();

        /*
         * Garante carregamento inicial mesmo quando não existem
         * opções guardadas em localStorage.
         */
        this.$nextTick(() => {
            this.searchTemplate();
        });
    },

    beforeDestroy() {
        clearTimeout(this.searchTimeout);
    },

    methods: {
        /*
         * ===============================================================
         * LOCAL STORAGE
         * ===============================================================
         */
        createTemplate() {
            this.$router.push(
                "/configuracoes/templates/registar"
            );
        },

        fillBaseData() {
            const key =
                "odc-templates-" +
                this.$store.state.user.id;

            if (localStorage[key] === undefined) {
                return;
            }

            try {
                const data =
                    JSON.parse(localStorage[key]);

                this.filter =
                    data.filter || {};

                if (
                    data.options &&
                    typeof data.options === "object"
                ) {
                    this.options = {
                        ...this.options,
                        ...data.options
                    };
                }
            } catch (error) {
                console.error(
                    "Erro ao recuperar filtros dos templates:",
                    error
                );
            }
        },

        /*
         * ===============================================================
         * PESQUISA
         * ===============================================================
         */

        searchTemplate(event, force = false) {
            if (
                event &&
                event.type === "submit"
            ) {
                this.options.page = 1;
            }

            if (this.loading && !force) {
                return;
            }

            this.loading = true;
            this.searching = true;

            const filter = {
                ...this.filter
            };

            const request = {
                sortBy: this.options.sortBy || [],
                sortDesc: this.options.sortDesc || [],
                page: this.options.page || 1,
                limit: this.options.itemsPerPage || 10,

                /*
                * Evita reaproveitamento/cache do mesmo GET.
                */
                _refresh: Date.now()
            };

            Object.assign(
                request,
                filter
            );

            const key =
                "odc-templates-" +
                this.$store.state.user.id;

            localStorage[key] =
                JSON.stringify({
                    filter: this.filter,
                    options: this.options
                });

            Template.search(request)
                .then(response => {
                    const data =
                        response.data || {};

                    this.total =
                        data.total || 0;

                    this.items =
                        Array.isArray(data.data)
                            ? data.data
                            : [];

                    this.lastUpdatedAt =
                        new Date();
                })
                .catch(error => {
                    console.error(
                        "Erro ao carregar templates:",
                        error
                    );
                })
                .finally(() => {
                    this.searching = false;
                    this.loading = false;
                });
        },

        /*
         * ===============================================================
         * REFRESH MANUAL
         * ===============================================================
         */

        refreshTable() {
            this.searchTemplate(null, true);
        },

        /*
         * ===============================================================
         * RESET
         * ===============================================================
         */

        resetTemplate() {
            this.filter = {};

            this.options = {
                ...this.options,
                page: 1
            };

            this.searchTemplate();
        },

        /*
         * ===============================================================
         * EDITAR
         * ===============================================================
         */

        viewTemplate(item) {
            if (
                !item ||
                !item.id
            ) {
                return;
            }

            this.$router.push(
                "templates/editar/" +
                item.id
            );
        },

        /*
         * ===============================================================
         * ELIMINAR
         * ===============================================================
         */

        deleteTemplate(item) {
            if (
                !item ||
                !item.id
            ) {
                return;
            }

            this.loading = true;

            this.$store
                .dispatch(
                    "deleteTemplate",
                    item.id
                )
                .then(() => {
                    this.success = true;

                    this.updateDialog.show =
                        false;

                    return this.searchTemplate();
                })
                .catch(error => {
                    console.error(
                        "Erro ao eliminar template:",
                        error
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        /*
         * ===============================================================
         * DIALOG
         * ===============================================================
         */

        onSuccess() {
            this.success = false;
        },

        /*
         * ===============================================================
         * HELPERS
         * ===============================================================
         */

        getUserInitials(name) {
            if (!name) {
                return "?";
            }

            const pieces =
                name
                    .trim()
                    .split(/\s+/)
                    .filter(Boolean);

            if (!pieces.length) {
                return "?";
            }

            if (pieces.length === 1) {
                return pieces[0]
                    .substring(0, 2)
                    .toUpperCase();
            }

            return (
                pieces[0][0] +
                pieces[pieces.length - 1][0]
            ).toUpperCase();
        }
    }
};
</script>

<style scoped>
/* ====================================================================== */
/* PÁGINA                                                                 */
/* ====================================================================== */

.templates-page {
    padding-bottom: 48px;
}

.templates-top {
    margin-top: 8px;
}


/* ====================================================================== */
/* BREADCRUMBS                                                            */
/* ====================================================================== */

.templates-breadcrumbs {
    opacity: 0.82;
}

.templates-breadcrumbs :deep(.v-breadcrumbs__item) {
    font-size: 13px;
    font-weight: 500;
}


/* ====================================================================== */
/* TÍTULO                                                                 */
/* ====================================================================== */

.templates-titlebar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    margin-top: 8px;
}

.templates-title {
    display: flex;
    align-items: center;
    gap: 14px;
}

.templates-title h1 {
    font-size: 30px;
    font-weight: 800;
    letter-spacing: -0.7px;
    line-height: 1.15;
}

.templates-subtitle {
    margin-top: 4px;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.52);
}

.templates-title__icon {
    width: 48px;
    height: 48px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 14px;

    background:
        linear-gradient(
            135deg,
            rgba(25, 118, 210, 0.12),
            rgba(25, 118, 210, 0.04)
        );

    transition:
        transform 0.25s ease,
        box-shadow 0.25s ease;
}

.templates-title:hover .templates-title__icon {
    transform: translateY(-2px) rotate(-2deg);

    box-shadow:
        0 8px 18px
        rgba(25, 118, 210, 0.12);
}


/* ====================================================================== */
/* BOTÃO REFRESH                                                          */
/* ====================================================================== */

.refresh-btn {
    border-radius: 10px;
    text-transform: none;
    font-weight: 700;
    letter-spacing: 0.1px;

    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;
}

.refresh-btn:hover {
    transform: translateY(-2px);

    box-shadow:
        0 6px 16px
        rgba(25, 118, 210, 0.15);
}

.refresh-icon--spinning {
    animation: template-spin 0.8s linear infinite;
}

@keyframes template-spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}


/* ====================================================================== */
/* CARD PRINCIPAL                                                         */
/* ====================================================================== */

.templates-card {
    position: relative;

    overflow: hidden;

    border-radius: 18px !important;

    border:
        1px solid
        rgba(0, 0, 0, 0.055);

    box-shadow:
        0 10px 35px
        rgba(0, 0, 0, 0.09) !important;

    transition:
        transform 0.25s ease,
        box-shadow 0.25s ease;
}

.templates-card:hover {
    box-shadow:
        0 14px 42px
        rgba(0, 0, 0, 0.11) !important;
}


/* ====================================================================== */
/* CARD HEADER                                                            */
/* ====================================================================== */

.templates-card__header {
    position: relative;

    min-height: 78px;

    padding:
        16px
        22px !important;

    overflow: hidden;
}

.templates-card__header::after {
    content: "";

    position: absolute;
    width: 220px;
    height: 220px;

    top: -150px;
    right: 140px;

    border-radius: 50%;

    background:
        rgba(255, 255, 255, 0.06);

    pointer-events: none;
}

.header-icon {
    width: 42px;
    height: 42px;

    margin-right: 13px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 12px;

    background:
        rgba(255, 255, 255, 0.14);

    backdrop-filter: blur(4px);
}

.header-subtitle {
    margin-top: 1px;

    font-size: 12px;
    font-weight: 400;

    opacity: 0.78;
}

.templates-count-chip {
    position: relative;

    z-index: 2;

    border-radius: 999px !important;

    font-weight: 700;
}

.last-updated {
    position: relative;

    z-index: 2;

    align-items: center;

    font-size: 11px;

    opacity: 0.8;
}


/* ====================================================================== */
/* CARD BODY                                                              */
/* ====================================================================== */

.templates-card__body {
    padding:
        0
        22px
        22px !important;
}


/* ====================================================================== */
/* TOOLBAR                                                                */
/* ====================================================================== */

.table-toolbar {
    min-height: 72px;

    display: flex;
    align-items: center;
    justify-content: space-between;

    padding:
        14px
        4px;

    border-bottom:
        1px solid
        rgba(0, 0, 0, 0.06);
}

.table-toolbar__info {
    display: flex;
    align-items: center;
}

.table-toolbar__icon {
    width: 34px;
    height: 34px;

    margin-right: 10px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 9px;

    background:
        rgba(25, 118, 210, 0.07);
}

.table-toolbar__title {
    font-size: 14px;
    font-weight: 700;

    color:
        rgba(0, 0, 0, 0.76);
}

.table-toolbar__subtitle {
    margin-top: 1px;

    font-size: 11px;

    color:
        rgba(0, 0, 0, 0.46);
}

.toolbar-refresh {
    text-transform: none;

    font-weight: 700;

    border-radius: 8px;
}


/* ====================================================================== */
/* TABELA                                                                 */
/* ====================================================================== */

.templates-table-wrapper {
    overflow: hidden;

    border-radius:
        0
        0
        14px
        14px;
}

.templates-table {
    background: transparent !important;
}

.templates-table :deep(.v-data-table__wrapper) {
    overflow-x: auto;
}


/* Cabeçalhos */

.templates-table :deep(thead th) {
    height: 54px !important;

    font-size: 12px !important;
    font-weight: 800 !important;

    color:
        rgba(0, 0, 0, 0.63) !important;

    letter-spacing: 0.3px;

    text-transform: uppercase;

    background:
        rgba(0, 0, 0, 0.018);
}


/* Linhas */

.templates-table :deep(tbody tr) {
    position: relative;

    transition:
        background 0.18s ease,
        transform 0.18s ease,
        box-shadow 0.18s ease;
}

.templates-table :deep(tbody tr:hover) {
    background:
        rgba(25, 118, 210, 0.035) !important;
}

.templates-table :deep(tbody td) {
    height: 68px !important;

    border-bottom:
        1px solid
        rgba(0, 0, 0, 0.05) !important;
}


/* ====================================================================== */
/* OBRA                                                                   */
/* ====================================================================== */

.work-cell {
    display: flex;
    align-items: center;
    gap: 12px;
}

.work-cell__icon {
    flex-shrink: 0;

    width: 34px;
    height: 34px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 9px;

    background:
        rgba(25, 118, 210, 0.075);

    transition:
        transform 0.2s ease,
        background 0.2s ease;
}

.templates-table
    :deep(tbody tr:hover)
    .work-cell__icon {
    transform: scale(1.07);

    background:
        rgba(25, 118, 210, 0.12);
}

.work-cell__name {
    font-size: 13px;
    font-weight: 650;

    color:
        rgba(0, 0, 0, 0.78);
}

.work-cell__code {
    margin-top: 2px;

    font-size: 11px;

    color:
        rgba(0, 0, 0, 0.44);
}


/* ====================================================================== */
/* UTILIZADOR                                                             */
/* ====================================================================== */

.user-cell {
    display: flex;
    align-items: center;
    gap: 10px;
}

.user-avatar {
    flex-shrink: 0;

    width: 34px;
    height: 34px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 50%;

    background:
        linear-gradient(
            135deg,
            rgba(25, 118, 210, 1),
            rgba(66, 165, 245, 1)
        );

    color: white;

    font-size: 11px;
    font-weight: 800;

    box-shadow:
        0 4px 10px
        rgba(25, 118, 210, 0.18);

    transition:
        transform 0.2s ease;
}

.templates-table
    :deep(tbody tr:hover)
    .user-avatar {
    transform: scale(1.07);
}

.user-cell__name {
    font-size: 13px;
    font-weight: 600;

    color:
        rgba(0, 0, 0, 0.75);
}

.user-cell__code {
    margin-top: 1px;

    font-size: 10px;

    color:
        rgba(0, 0, 0, 0.42);
}


/* ====================================================================== */
/* AÇÕES                                                                  */
/* ====================================================================== */

.actions-cell {
    min-height: 40px;

    display: flex;
    align-items: center;
    justify-content: center;

    gap: 7px;
}

.action-btn {
    transition:
        transform 0.18s ease,
        background 0.18s ease;
}

.action-btn--edit {
    color:
        #1976d2 !important;

    background:
        rgba(25, 118, 210, 0.075);
}

.action-btn--edit:hover {
    transform: translateY(-2px) scale(1.06);

    background:
        rgba(25, 118, 210, 0.13);
}

.remove-wrapper {
    display: flex;
    align-items: center;

    transition:
        transform 0.18s ease;
}

.remove-wrapper:hover {
    transform: translateY(-2px);
}


/* ====================================================================== */
/* BLOQUEADO                                                              */
/* ====================================================================== */

.locked-chip {
    display: inline-flex;
    align-items: center;

    padding:
        6px
        10px;

    border-radius: 999px;

    color:
        rgba(0, 0, 0, 0.52);

    background:
        rgba(0, 0, 0, 0.055);

    font-size: 11px;
    font-weight: 700;
}


/* ====================================================================== */
/* EMPTY                                                                  */
/* ====================================================================== */

.empty-state {
    padding:
        52px
        20px;

    text-align: center;
}

.empty-state__icon {
    margin-bottom: 10px;
}

.empty-state__title {
    font-size: 15px;
    font-weight: 700;

    color:
        rgba(0, 0, 0, 0.65);
}

.empty-state__text {
    margin-top: 4px;

    font-size: 12px;

    color:
        rgba(0, 0, 0, 0.44);
}


/* ====================================================================== */
/* PAGINAÇÃO                                                              */
/* ====================================================================== */

.templates-table
    :deep(.v-data-footer) {
    min-height: 62px;

    border-top:
        1px solid
        rgba(0, 0, 0, 0.055);

    font-size: 12px;
}


/* ====================================================================== */
/* DARK MODE                                                              */
/* ====================================================================== */

.theme--dark .templates-subtitle,
.theme--dark .table-toolbar__subtitle,
.theme--dark .work-cell__code,
.theme--dark .user-cell__code {
    color:
        rgba(255, 255, 255, 0.5);
}

.theme--dark .table-toolbar__title,
.theme--dark .work-cell__name,
.theme--dark .user-cell__name {
    color:
        rgba(255, 255, 255, 0.82);
}

.theme--dark .templates-card {
    border-color:
        rgba(255, 255, 255, 0.06);
}

.theme--dark .templates-table :deep(thead th) {
    color:
        rgba(255, 255, 255, 0.64) !important;

    background:
        rgba(255, 255, 255, 0.025);
}

.theme--dark .templates-table :deep(tbody tr:hover) {
    background:
        rgba(255, 255, 255, 0.035) !important;
}

.theme--dark .table-toolbar {
    border-bottom-color:
        rgba(255, 255, 255, 0.07);
}


/* ====================================================================== */
/* MOBILE                                                                 */
/* ====================================================================== */

@media (max-width: 960px) {
    .templates-titlebar {
        align-items: flex-start;
        flex-direction: column;
    }

    .templates-titlebar__actions {
        width: 100%;
    }

    .create-btn,
    .refresh-btn {
        flex: 1;
    }

    .templates-title h1 {
        font-size: 25px;
    }

    .templates-card__header {
        min-height: 72px;
    }

    .templates-card__body {
        padding:
            0
            12px
            14px !important;
    }
}


/* ====================================================================== */
/* ACESSIBILIDADE                                                        */
/* ====================================================================== */

@media (prefers-reduced-motion: reduce) {
    .templates-title__icon,
    .refresh-btn,
    .templates-card,
    .work-cell__icon,
    .user-avatar,
    .action-btn,
    .remove-wrapper,
    .templates-table :deep(tbody tr) {
        transition: none !important;
    }

    .refresh-icon--spinning {
        animation: none !important;
    }
}
.templates-titlebar__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

.create-btn {
    border-radius: 10px;
    text-transform: none;
    font-weight: 700;
    letter-spacing: 0.1px;

    box-shadow:
        0 5px 14px
        rgba(25, 118, 210, 0.22);

    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;
}

.create-btn:hover {
    transform: translateY(-2px);

    box-shadow:
        0 8px 18px
        rgba(25, 118, 210, 0.28);
}
</style>